import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import MainNavigation from '../../components/ui/MainNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import CategoryForm from './components/CategoryForm';
import CategoryHierarchy from './components/CategoryHierarchy';
import CategoryImageUpload from './components/CategoryImageUpload';
import Icon from '../../components/AppIcon';

const AddCategory = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentCategory: '',
    slug: '',
    image: null,
    color: '#3B82F6',
    isVisible: true,
    sortOrder: 0,
    seoTitle: '',
    seoDescription: '',
    seoKeywords: ''
  });

  // Mock existing categories for parent selection
  const [existingCategories] = useState([
    { id: 'electronics', name: 'Electronics', level: 0, children: ['smartphones', 'laptops'] },
    { id: 'smartphones', name: 'Smartphones', level: 1, parent: 'electronics' },
    { id: 'laptops', name: 'Laptops', level: 1, parent: 'electronics' },
    { id: 'clothing', name: 'Clothing', level: 0, children: ['mens', 'womens'] },
    { id: 'mens', name: "Men's Fashion", level: 1, parent: 'clothing' },
    { id: 'womens', name: "Women's Fashion", level: 1, parent: 'clothing' },
    { id: 'books', name: 'Books', level: 0, children: [] },
    { id: 'home-garden', name: 'Home & Garden', level: 0, children: ['furniture', 'decor'] },
    { id: 'furniture', name: 'Furniture', level: 1, parent: 'home-garden' },
    { id: 'decor', name: 'Home Decor', level: 1, parent: 'home-garden' }
  ]);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('Token');
    if (!token) {
      navigate('/login');
      return;
    }

    // Set mock user data
    setUser({
      name: 'Admin User',
      email: 'admin@crm.com',
      role: 'Administrator'
    });
  }, [navigate]);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Auto-generate slug from name
    if (field === 'name' && value) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const validateCategoryHierarchy = (parentId, categoryName) => {
    // Prevent circular references and validate hierarchy
    if (!parentId) return true;

    const parent = existingCategories.find(cat => cat.id === parentId);
    if (!parent) return false;

    // Check for name conflicts at the same level
    const siblings = existingCategories?.filter(cat => cat.parent === parentId);
    const nameConflict = siblings.some(cat => 
      cat.name.toLowerCase() === categoryName.toLowerCase()
    );

    return !nameConflict;
  };

  const handleCreateCategory = async (action = 'create') => {
    setLoading(true);
    try {
      // Validate required fields
      if (!formData.name.trim()) {
        throw new Error('Category name is required');
      }

      // Validate hierarchy
      if (!validateCategoryHierarchy(formData.parentCategory, formData.name)) {
        throw new Error('Category name conflicts with existing category at this level');
      }

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      const categoryData = {
        ...formData,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        level: formData.parentCategory ? 
          existingCategories.find(cat => cat.id === formData.parentCategory)?.level + 1 || 1 : 0
      };

      console.log('Category data:', categoryData);

      switch (action) {
        case 'create_and_add_products': alert('Category created successfully! Redirecting to add products...');
          // Navigate to add products with category pre-selected
          navigate('/add-product', { state: { selectedCategory: categoryData.id } });
          break;
        default:
          alert('Category created successfully!');
          navigate('/category-management');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const hasChanges = Object.keys(formData).some(key => {
      if (key === 'color' || key === 'isVisible' || key === 'sortOrder') return false;
      return formData[key] && formData[key].toString().trim().length > 0;
    });

    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        navigate('/category-management');
      }
    } else {
      navigate('/category-management');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Icon name="Loader2" size={24} className="animate-spin text-primary" />
          <span className="text-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <NavigationHeader
        user={user}
        onMenuToggle={handleSidebarToggle}
        sidebarCollapsed={sidebarCollapsed}
      />

      {/* Main Navigation Sidebar */}
      <MainNavigation
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      {/* Main Content */}
      <main
        className={`transition-layout pt-16 ${
          sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-60'
        }`}
      >
        {/* Breadcrumb Navigation */}
        <BreadcrumbNavigation />

        {/* Page Content */}
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-2">Add New Category</h1>
                <p className="text-muted-foreground">
                  Create a new product category with proper hierarchy and organization.
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm border border-border rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          {/* Form Container */}
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Form - Takes 2 columns */}
              <div className="lg:col-span-2 space-y-6">
                <CategoryForm
                  formData={formData}
                  onChange={handleFormChange}
                  existingCategories={existingCategories}
                />
              </div>

              {/* Sidebar - Takes 1 column */}
              <div className="space-y-6">
                {/* Category Image Upload */}
                <CategoryImageUpload
                  image={formData.image}
                  onChange={(image) => handleFormChange('image', image)}
                  color={formData.color}
                  onColorChange={(color) => handleFormChange('color', color)}
                />

                {/* Hierarchy Visualization */}
                <CategoryHierarchy
                  existingCategories={existingCategories}
                  selectedParent={formData.parentCategory}
                  newCategoryName={formData.name}
                />

                {/* Action Buttons */}
                <div className="bg-white rounded-lg border border-border p-6 space-y-4">
                  <h3 className="text-lg font-semibold text-foreground">Actions</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => handleCreateCategory('create')}
                      disabled={loading}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Icon name="FolderPlus" size={16} className="mr-2" />
                          Create Category
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleCreateCategory('create_and_add_products')}
                      disabled={loading}
                      className="w-full bg-success text-success-foreground hover:bg-success/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      <Icon name="Package" size={16} className="mr-2" />
                      Create & Add Products
                    </button>
                  </div>
                </div>

                {/* Category Settings */}
                <div className="bg-white rounded-lg border border-border p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Category Settings</h3>
                  <div className="space-y-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isVisible}
                        onChange={(e) => handleFormChange('isVisible', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm">Visible to customers</span>
                    </label>
                    
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Sort Order
                      </label>
                      <input
                        type="number"
                        value={formData.sortOrder}
                        onChange={(e) => handleFormChange('sortOrder', parseInt(e.target.value) || 0)}
                        min="0"
                        className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        placeholder="0"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Lower numbers appear first
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AddCategory;